import { useEffect, useState } from "react";
import { db } from "../config/firebaseConfig";
import { doc, onSnapshot } from "firebase/firestore";

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const getByPath = (source, path) => {
  if (!isPlainObject(source) || !path) {
    return undefined;
  }

  return path
    .split(".")
    .reduce((acc, segment) => (acc == null ? undefined : acc[segment]), source);
};

const normalizeCollection = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (!isPlainObject(value)) {
    return [];
  }

  const candidateArrays = [
    value.items,
    value.images,
    value.media,
    value.list,
    value.data,
  ];
  const directArray = candidateArrays.find((entry) => Array.isArray(entry));

  if (directArray) {
    return directArray;
  }

  const objectValues = Object.values(value);
  if (
    objectValues.length > 0 &&
    objectValues.every(
      (entry) =>
        typeof entry === "string" ||
        isPlainObject(entry) ||
        Array.isArray(entry),
    )
  ) {
    return objectValues;
  }

  return [];
};

const resolveCollection = (data, paths) => {
  for (const path of paths) {
    const value = getByPath(data, path);
    const normalized = normalizeCollection(value);
    if (normalized.length > 0) {
      return { value: normalized, sourcePath: path };
    }
  }

  const fallbackPath = paths[0] || "";
  const fallbackValue = normalizeCollection(getByPath(data, fallbackPath));
  return { value: fallbackValue, sourcePath: fallbackPath || null };
};

const GALLERY_SECTION_PATHS = {
  inauguration: [
    "inauguration",
    "inaugurationCeremony",
    "campusGallery",
    "gallery.inauguration",
    "galleries.inauguration",
    "sections.inauguration",
    "sections.inauguration.items",
    "campusMoments.inauguration",
  ],
  schoolLife: [
    "schoolLife",
    "ourSchoolLife",
    "school_life",
    "campusMoments",
    "campusLife",
    "gallery.schoolLife",
    "galleries.schoolLife",
    "sections.schoolLife",
    "sections.schoolLife.items",
    "campusMoments.schoolLife",
  ],
  latestUpdates: [
    "latestUpdates",
    "updates",
    "notices",
    "gallery.latestUpdates",
    "sections.latestUpdates",
  ],
};

export const useGalleryImages = () => {
  const shouldLogDebug =
    process.env.NODE_ENV !== "production" ||
    (typeof window !== "undefined" &&
      (() => {
        const params = new URLSearchParams(window.location.search);
        return (
          params.get("debugGallery") === "1" ||
          params.get("debug") === "gallery"
        );
      })());

  const [images, setImages] = useState({
    inauguration: [],
    schoolLife: [],
    latestUpdates: [],
  });
  const [sources, setSources] = useState({
    inauguration: null,
    schoolLife: null,
    latestUpdates: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorDetails, setErrorDetails] = useState(null);
  const [docExists, setDocExists] = useState(null);

  useEffect(() => {
    const galleryRef = doc(db, "siteSettings", "galleryImages");

    const unsubscribe = onSnapshot(
      galleryRef,
      (snapshot) => {
        setDocExists(snapshot.exists());
        setError("");
        setErrorDetails(null);

        if (snapshot.exists()) {
          const data = snapshot.data();

          const inauguration = resolveCollection(
            data,
            GALLERY_SECTION_PATHS.inauguration,
          );
          const schoolLife = resolveCollection(
            data,
            GALLERY_SECTION_PATHS.schoolLife,
          );
          const latestUpdates = resolveCollection(
            data,
            GALLERY_SECTION_PATHS.latestUpdates,
          );

          setImages({
            inauguration: inauguration.value,
            schoolLife: schoolLife.value,
            latestUpdates: latestUpdates.value,
          });
          setSources({
            inauguration: inauguration.sourcePath,
            schoolLife: schoolLife.sourcePath,
            latestUpdates: latestUpdates.sourcePath,
          });

          if (shouldLogDebug) {
            console.log("[GalleryDebug] Firebase snapshot parsed", {
              docPath: "siteSettings/galleryImages",
              rawKeys: Object.keys(data || {}),
              resolvedSourcePaths: {
                inauguration: inauguration.sourcePath,
                schoolLife: schoolLife.sourcePath,
                latestUpdates: latestUpdates.sourcePath,
              },
              counts: {
                inauguration: inauguration.value.length,
                schoolLife: schoolLife.value.length,
                latestUpdates: latestUpdates.value.length,
              },
              sample: {
                inauguration: inauguration.value.slice(0, 2),
                schoolLife: schoolLife.value.slice(0, 2),
              },
            });
          }
        } else {
          setImages({
            inauguration: [],
            schoolLife: [],
            latestUpdates: [],
          });
          setSources({
            inauguration: null,
            schoolLife: null,
            latestUpdates: null,
          });

          if (shouldLogDebug) {
            console.log("[GalleryDebug] galleryImages doc missing", {
              docPath: "siteSettings/galleryImages",
            });
          }
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error loading gallery images:", error);
        setDocExists(null);
        setErrorDetails({
          code: String(error?.code || ""),
          name: String(error?.name || ""),
          message: String(error?.message || ""),
        });
        setError(error?.message || "Unable to load gallery images.");
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [shouldLogDebug]);

  return { images, sources, loading, error, errorDetails, docExists };
};
